import { clone } from 'lodash-es'
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform'
import { VAxios } from './Axios'
import { checkStatus } from './checkStatus'
import { useGlobSetting } from '/@/hooks/setting'
import { ContentTypeEnum } from '/@/enums/httpEnum'
import { getToken } from '/@/utils/auth'
import { deepMerge } from '/@/utils'
import {
  createTransformRequestHook,
  createBeforeRequestHook,
  createResponseInterceptors,
  createResponseInterceptorsCatch,
} from './transform'

const globSetting = useGlobSetting()

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: 处理请求数据。如果数据不是预期格式，可直接抛出错误
   */
  transformRequestHook: createTransformRequestHook(),

  /**
   * @description: 请求之前处理config
   */
  beforeRequestHook: createBeforeRequestHook(),

  /**
   * @description: 请求拦截器处理
   */
  requestInterceptors: (config, options) => {
    // 请求之前处理config
    const token = getToken()
    const recordableConfig = config as Recordable
    if (token && recordableConfig?.requestOptions?.withToken !== false) {
      // jwt token
      recordableConfig.headers.Authorization = options.authenticationScheme
        ? `${options.authenticationScheme} ${token}`
        : token
    }
    // 保存 authenticationScheme 到 requestOptions 中，供 token 刷新时使用
    if (!recordableConfig.requestOptions) {
      recordableConfig.requestOptions = {}
    }
    recordableConfig.requestOptions.authenticationScheme = options.authenticationScheme
    return config
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: createResponseInterceptors(),

  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: createResponseInterceptorsCatch(checkStatus),
}

function createAxios(opt?: Partial<CreateAxiosOptions>) {
  return new VAxios(
    deepMerge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        authenticationScheme: '',
        timeout: 10 * 1000,
        // 基础接口地址
        baseURL: globSetting.apiUrl,

        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform: clone(transform),
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 默认将prefix 添加到url
          joinPrefix: false,
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: true,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'none',
          // 接口地址
          apiUrl: globSetting.apiUrl,
          // 接口拼接地址
          urlPrefix: '',
          //  是否加入时间戳
          joinTime: false,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
        },
      },
      opt || {},
    ),
  )
}
export const defHttp = createAxios()

// other api url
// export const otherHttp = createAxios({
//   requestOptions: {
//     apiUrl: 'xxx',
//     urlPrefix: 'xxx',
//   },
// });
